import { useEffect, useState, useRef } from 'react';

type UseElapsedTimerProps = {
  startTime?: number;
  getCurrentTime: () => number;
};

type UseElapsedTimerReturn = {
  elapsedMilliseconds: number;
  elapsedSeconds: number;
};

export const useElapsedTimer = ({
  startTime = Date.now(),
  getCurrentTime,
}: UseElapsedTimerProps): UseElapsedTimerReturn => {
  const initialStartTime = useRef(startTime).current;
  const lastSecondsRef = useRef<number>(-1);

  // Track elapsed time in milliseconds
  const [elapsed, setElapsed] = useState(() => {
    const currentTime = getCurrentTime();
    return currentTime - initialStartTime;
  });

  useEffect(() => {
    const updateElapsed = () => {
      const currentTime = getCurrentTime();
      const newElapsed = currentTime - initialStartTime;
      const newSeconds = Math.floor(newElapsed / 1000);

      // Only update if seconds changed to avoid unnecessary re-renders
      if (newSeconds !== lastSecondsRef.current) {
        lastSecondsRef.current = newSeconds;
        setElapsed(newElapsed);
      }
    };

    // Update immediately
    updateElapsed();

    // Set up interval for continuous updates using getCurrentTime
    const interval = setInterval(updateElapsed, 1000);

    return () => clearInterval(interval);
  }, [getCurrentTime, initialStartTime]);

  return {
    elapsedMilliseconds: elapsed,
    elapsedSeconds: Math.floor(elapsed / 1000),
  };
};
